import type {
  DetailedHTMLProps,
  HTMLAttributes,
  ImgHTMLAttributes,
} from "react";
import { forwardRef } from "react";

import { tw } from "@/shared/utils";
import { Skeleton } from "./skeleton";

const Root = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw("flex flex-col gap-1", className)}
      {...props}
    />
  ),
);
Root.displayName = "UploadedFile.Root";

const Container = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "flex h-20 gap-4 rounded-2xl border border-neutral-900 bg-white px-4 py-3",
        className,
      )}
      {...props}
    />
  ),
);
Container.displayName = "UploadedFile.Container";

const Preview = forwardRef<
  HTMLImageElement,
  DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
>(({ className, alt, ...props }, ref) => (
  <img
    alt={alt}
    ref={ref}
    className={tw("aspect-square h-full rounded-md object-cover", className)}
    {...props}
  />
));
Preview.displayName = "UploadedFile.Preview";

const Content = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "flex h-full grow flex-col items-start justify-center gap-1",
        className,
      )}
      {...props}
    />
  ),
);
Content.displayName = "Header.Content";

const Title = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h6
    ref={ref}
    className={tw(
      "max-w-52 truncate text-sm text-neutral-900 md:max-w-80",
      className,
    )}
    {...props}
  >
    {children}
  </h6>
));
Title.displayName = "UploadedFile.Title";

const Description = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={tw("text-xs text-neutral-600", className)}
    {...props}
  />
));
Description.displayName = "UploadedFile.Description";

const Actions = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={tw("self-center", className)} {...props} />
  ),
);
Actions.displayName = "UploadedFile.Actions";

const UploadedFileSkeleton = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={tw(
      "flex h-20 items-center gap-4 rounded-2xl border border-secondary-100 bg-secondary-50 px-4 py-3",
      className,
    )}
    {...props}
  >
    <div className="flex grow gap-3">
      <Skeleton className="size-6" />
      <Skeleton className="h-6 w-full max-w-40" />
    </div>

    <div className="flex basis-1/6 gap-3 justify-self-end">
      <Skeleton className="size-6 shrink-0" />
      <Skeleton className="h-6 w-full max-w-24" />
    </div>

    <Skeleton className="size-6" />
  </div>
));
UploadedFileSkeleton.displayName = "UploadedFile.Skeleton";

export const UploadedFileCard = {
  Root,
  Container,
  Preview,
  Content,
  Title,
  Description,
  Actions,
  Skeleton: UploadedFileSkeleton,
};
